import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Sparkle, HeartHandshake } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitePage() {
    const { code } = useParams();
    const [status, setStatus] = useState("loading");
    const [tier, setTier] = useState(null);
    const [email, setEmail] = useState("");
    const [form, setForm] = useState("idle");
    const [message, setMessage] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/invite/${code}`)
            .then((r) => {
                setTier(r.data.tier_name);
                setStatus("valid");
            })
            .catch(() => setStatus("invalid"));
    }, [code]);

    const submit = async (e) => {
        e.preventDefault();
        setForm("loading");
        try {
            const { data } = await axios.post(`${API}/invite/${code}/join`, { email });
            setMessage(data.message);
            setForm("success");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
            setForm("error");
        }
    };

    return (
        <main data-testid="invite-page" className="flex min-h-screen items-center justify-center bg-cream px-6 text-navy">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                className="w-full max-w-lg rounded-3xl border-2 border-warmgray bg-ivory p-10 text-center shadow-xl shadow-navy/5"
            >
                <Link to="/" data-testid="invite-logo" aria-label="Ago GenApp home">
                    <img src="/logo.png" alt="Ago GenApp" className="mx-auto h-12 w-auto" />
                </Link>

                {status === "loading" && (
                    <div className="mt-8 flex justify-center">
                        <Loader2 size={32} className="animate-spin text-lavender" />
                    </div>
                )}

                {status === "invalid" && (
                    <div data-testid="invite-invalid">
                        <div className="mt-8 flex justify-center">
                            <HeartHandshake size={32} className="text-peach" />
                        </div>
                        <h1 className="mt-5 font-display text-2xl md:text-3xl">This invite link isn’t valid.</h1>
                        <p className="mt-3 text-sm leading-relaxed text-navy">
                            It may have expired or been typed incorrectly. You can still join the public waitlist.
                        </p>
                        <Link
                            data-testid="invite-invalid-home"
                            to="/"
                            className="btn-gradient mt-7 inline-block rounded-full px-8 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            Go to Ago GenApp
                        </Link>
                    </div>
                )}

                {status === "valid" && form !== "success" && (
                    <div data-testid="invite-valid">
                        <p className="mt-8 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">
                            A family invitation{tier ? ` · ${tier} circle` : ""}
                        </p>
                        <h1 className="mt-4 font-display text-2xl leading-tight md:text-3xl">
                            You’ve been invited into a family’s <span className="italic">legacy.</span>
                        </h1>
                        <p className="mt-4 text-sm leading-relaxed text-navy">
                            A Founding Supporter has invited you into their shared legacy space — with early access to the
                            private beta when it opens. Leave your email to accept.
                        </p>
                        <form onSubmit={submit} className="mt-7 flex flex-col gap-3">
                            <input
                                data-testid="invite-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@yourfamily.com"
                                aria-label="Email address"
                                className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-4 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                            />
                            <button
                                data-testid="invite-accept-button"
                                type="submit"
                                disabled={form === "loading"}
                                className="btn-gradient flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                            >
                                {form === "loading" && <Loader2 size={15} className="animate-spin" />}
                                Accept the Invitation
                            </button>
                        </form>
                        {form === "error" && (
                            <p data-testid="invite-error" className="mt-3 text-sm text-red-700">{message}</p>
                        )}
                    </div>
                )}

                {status === "valid" && form === "success" && (
                    <div data-testid="invite-success">
                        <div className="mt-8 flex justify-center">
                            <CheckCircle2 size={36} className="text-lavender" />
                        </div>
                        <h1 className="mt-5 font-display text-2xl md:text-3xl">Welcome to the family.</h1>
                        <p className="mt-3 text-sm leading-relaxed text-navy">{message}</p>
                        <Link
                            data-testid="invite-success-home"
                            to="/"
                            className="mt-7 inline-block rounded-full border-2 border-navy px-8 py-3.5 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            Explore Ago GenApp
                        </Link>
                    </div>
                )}
            </motion.div>
        </main>
    );
}
